/*
  Video tutorial detail: the player facade, chapter seeking, and the add-all
  materials path.

  Three jobs, one element, because all three need to reach across the two
  columns and the section is the only common ancestor.

  What works without this file, and must keep working:

    - The poster, the title, the tags and the written steps are all server
      markup. The timecode buttons ship `hidden` and are revealed here, so a
      reader with script blocked never meets a control that does nothing; the
      plain timecode beside each step is what they get instead.
    - The materials form is a real `POST` to /cart/add. This file cancels the
      submit and goes through the theme's own `cart:add` event instead, which
      `assets/cart.js` already listens on — the same seam the kit builder and
      quick-add use. With the script gone the form posts and the cart page
      loads.

  What this file cannot do, and does not pretend to: read the playhead of a
  third-party iframe. Once a YouTube or Vimeo embed is in the frame the progress
  bar is gone with the poster, and chapter state is whatever the reader last
  clicked. A hosted `<video>` does report real time, and that path is wired.
*/
(function () {
  const STORE_PREFIX = 'video-tutorial:';

  function readProgress(handle) {
    try {
      const raw = window.localStorage.getItem(STORE_PREFIX + handle);
      const seconds = Number(raw);
      return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    } catch (error) {
      return 0;
    }
  }

  function writeProgress(handle, seconds) {
    try {
      window.localStorage.setItem(STORE_PREFIX + handle, String(Math.round(seconds)));
    } catch (error) {
      /* Private window, blocked storage, or a full quota. Losing the resume
         point is not worth breaking the click that caused it. */
    }
  }

  function formatTime(seconds) {
    const total = Math.max(0, Math.round(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  }

  class VideoTutorialPlayer extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle || '';
      this.duration = Number(this.dataset.durationSeconds) || 0;

      try {
        this.chapters = JSON.parse(this.dataset.chapters || '[]');
      } catch (error) {
        /* Liquid built this string, so a parse failure means the metafield held
           something Liquid could not make sense of either. Seeking degrades to
           nothing; every other part of the page is unaffected. */
        this.chapters = [];
      }
      if (!Array.isArray(this.chapters)) this.chapters = [];

      /* The provider, the video id and the title live on the player *block's*
         root, not on this element: the section owns the coordinating element
         and the block owns the video. Reading them off `this.dataset` — which
         is what the first version did — gave undefined for all three, so
         `embedUrl` returned an empty string and the click silently did
         nothing. Measured in the browser 2026-09-07; that was the "play button
         does not work" report. */
      this.frame = this.querySelector('.vtp__frame');
      this.root = this.querySelector('[data-vtp-root]');
      this.playButton = this.querySelector('[data-vtp-play]');
      this.hostedTemplate = this.querySelector('[data-vtp-hosted]');
      this.fill = this.querySelector('[data-vtp-fill]');
      this.elapsed = this.querySelector('[data-vtp-elapsed]');
      this.seekButtons = Array.from(this.querySelectorAll('[data-vtp-seek]'));

      this.armSteps();
      this.restore();
      this.setupAccordions();

      if (this.playButton) {
        this.playButton.addEventListener('click', () => this.play(this.pending || 0));
      }
      this.addEventListener('click', (event) => this.onSeekClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
    }

    /* ── Facade ──────────────────────────────────────────────────────────── */

    armSteps() {
      /* Reveals the timecode buttons and hides their static twins. Done from
         script so the two never both show, and so a scriptless page shows the
         one that is honest. */
      this.seekButtons.forEach((button) => {
        button.hidden = false;
      });
      if (this.seekButtons.length) this.dataset.vtpArmed = 'true';
    }

    /* The rail panels ship as `<details open>`, so this only ever closes them —
       below the width where the layout is one column and a fully expanded rail
       would be a long scroll between the video and the written steps. With this
       file blocked they stay open at every width, which is the behaviour that
       shipped before the setting existed.

       Telling our own writes apart from the reader's is the whole difficulty
       here, and a synchronous "am I applying right now" flag does not do it:
       `details` queues its `toggle` event as a **task**, so the handler runs
       after the flag has already been cleared, and our own three closes look
       exactly like three reader clicks.

       So instead of timing, compare values. Each panel remembers the state we
       last wrote; a `toggle` whose result matches that was ours, and anything
       else came from the reader. That holds whenever the event arrives.

       This one is reasoned from the spec rather than measured: the pane's
       viewport emulation does not dispatch `matchMedia` change events, so
       crossing the breakpoint on a live page could not be exercised here. Load
       time is verified at both widths. See CLAUDE_HANDOFF.md. */
    setupAccordions() {
      const panels = Array.from(this.querySelectorAll('[data-vtd-accordion]'));
      if (!panels.length || !window.matchMedia) return;

      const query = window.matchMedia('(min-width: ' + (Number(this.dataset.collapseAt) || 1040) + 'px)');
      const touched = new WeakSet();
      const applied = new WeakMap();

      const apply = () => {
        panels.forEach((panel) => {
          /* A panel the reader has opened or closed themselves is theirs. */
          if (touched.has(panel)) return;
          panel.open = query.matches;
          applied.set(panel, query.matches);
        });
      };

      panels.forEach((panel) => {
        panel.addEventListener('toggle', () => {
          if (applied.get(panel) !== panel.open) touched.add(panel);
        });
      });

      apply();
      query.addEventListener('change', apply);
    }

    restore() {
      const seconds = readProgress(this.handle);
      if (!seconds) return;
      this.pending = seconds;
      this.mark(seconds);
      this.paint(seconds);
    }

    /* Swaps the facade for a real player, starting at `seconds`. */
    play(seconds) {
      const provider = this.root && this.root.dataset.vtpProvider;
      const start = Math.max(0, Math.round(seconds || 0));

      if (this.playing) {
        this.seek(start);
        return;
      }

      let node = null;

      if (provider === 'hosted' && this.hostedTemplate) {
        node = this.hostedTemplate.content.firstElementChild.cloneNode(true);
        this.video = node;
        node.currentTime = start;
        node.addEventListener('timeupdate', () => this.onTimeUpdate());
        node.autoplay = true;
      } else {
        const src = this.embedUrl(provider, start);
        if (!src) return;
        node = document.createElement('iframe');
        node.src = src;
        node.title = (this.root && this.root.dataset.vtpTitle) || '';
        node.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
        node.allowFullscreen = true;
        node.loading = 'eager';
        node.referrerPolicy = 'strict-origin-when-cross-origin';
      }

      if (!node) return;

      this.frame.appendChild(node);
      this.frame.dataset.vtpPlaying = 'true';
      this.playing = true;
      if (this.video) this.video.play().catch(() => {});
      writeProgress(this.handle, start);
    }

    embedUrl(provider, start) {
      const id = this.root && this.root.dataset.vtpVideoId;
      if (!id) return '';

      if (provider === 'youtube') {
        /* `youtube-nocookie` is the same player without the tracking cookie on
           a viewer who never presses play — which, behind a facade, is most of
           them. */
        const url = new URL('https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id));
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('rel', '0');
        if (start) url.searchParams.set('start', String(start));
        return url.toString();
      }

      if (provider === 'vimeo') {
        const url = new URL('https://player.vimeo.com/video/' + encodeURIComponent(id));
        url.searchParams.set('autoplay', '1');
        if (start) url.hash = 't=' + start + 's';
        return url.toString();
      }

      return '';
    }

    seek(seconds) {
      if (this.video) {
        this.video.currentTime = seconds;
        this.video.play().catch(() => {});
        return;
      }
      /* A third-party iframe cannot be told to jump without its own API, and
         loading one of those defeats the facade. Reloading the embed at the new
         start is the honest move: it is what the reader asked for, and it costs
         one request they have already opted into. */
      const src = this.embedUrl(this.root && this.root.dataset.vtpProvider, seconds);
      const iframe = this.frame.querySelector('iframe');
      if (src && iframe) iframe.src = src;
    }

    onTimeUpdate() {
      if (!this.video) return;
      const seconds = this.video.currentTime;
      this.mark(seconds);
      this.paint(seconds);
      /* Once a second is often enough for a resume point, and it keeps this off
         the write path of every timeupdate tick. */
      if (!this.lastWrite || Math.abs(seconds - this.lastWrite) >= 5) {
        this.lastWrite = seconds;
        writeProgress(this.handle, seconds);
      }
    }

    /* ── Chapters ────────────────────────────────────────────────────────── */

    onSeekClick(event) {
      const button = event.target.closest('[data-vtp-seek]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      const seconds = Number(button.dataset.vtpSeek) || 0;

      this.pending = seconds;
      this.mark(seconds);
      this.paint(seconds);
      this.play(seconds);
      writeProgress(this.handle, seconds);
    }

    /* Exactly one pressed control, in both columns at once: the chapter row and
       the written step for the same moment describe the same state, and a
       screen reader that found two pressed buttons would be told the page is in
       two places. */
    mark(seconds) {
      let activeSeconds = -1;
      this.chapters.forEach((chapter) => {
        if (chapter.seconds <= seconds + 0.5 && chapter.seconds > activeSeconds) {
          activeSeconds = chapter.seconds;
        }
      });

      this.seekButtons.forEach((button) => {
        const own = Number(button.dataset.vtpSeek);
        button.setAttribute('aria-pressed', own === activeSeconds ? 'true' : 'false');
      });
    }

    paint(seconds) {
      if (this.elapsed) this.elapsed.textContent = formatTime(seconds);
      if (!this.fill || !this.duration) return;
      const ratio = Math.min(1, Math.max(0, seconds / this.duration));
      this.fill.style.inlineSize = (ratio * 100).toFixed(2) + '%';
    }

    /* ── Add all materials ───────────────────────────────────────────────── */

    async onSubmit(event) {
      const form = event.target.closest('[data-vtm-form]');
      if (!form || !this.contains(form)) return;

      /* Only take over when the drawer is actually on the page. Without it the
         plain POST is the better path — it ends somewhere the reader can see
         their cart, rather than succeeding invisibly. */
      if (!document.querySelector('cart-drawer')) return;

      event.preventDefault();

      const button = form.querySelector('[data-vtm-submit]');
      const errorEl = form.querySelector('[data-vtm-error]');
      if (errorEl) errorEl.textContent = '';

      const items = [];
      const data = new FormData(form);
      /* Walked by index rather than by parsing keys, so the ids and quantities
         stay paired exactly as Liquid printed them. */
      for (let i = 0; data.has(`items[${i}][id]`); i += 1) {
        items.push({
          id: Number(data.get(`items[${i}][id]`)),
          quantity: Number(data.get(`items[${i}][quantity]`)) || 1
        });
      }
      if (!items.length) return;

      if (button) button.setAttribute('aria-busy', 'true');

      /* The drawer owns the request, the render and the error handling — the
         same path every other add in this theme takes. */
      document.dispatchEvent(new CustomEvent('cart:add', { detail: { items } }));

      /* The drawer reports failure by leaving the cart unchanged; it does not
         hand a rejection back through the event. Releasing the busy state after
         it has had a turn keeps the button from staying stuck. */
      window.setTimeout(() => {
        if (button) button.removeAttribute('aria-busy');
      }, 1200);
    }
  }

  if (!customElements.get('video-tutorial-player')) {
    customElements.define('video-tutorial-player', VideoTutorialPlayer);
  }
})();
